import {FilteringProperty} from "@amzn/awsui-components-react/polaris/property-filter/interfaces";
import {
    columnDefinitionToOption,
    configPropsToColumnDefinitions,
    configPropsToFilterProps,
    TableContentConfigProps,
    TEXT_FIELD_OPERATORS
} from "../tableConfig";
import {Box, Button, CollectionPreferencesProps, IconProps, Modal} from "@amzn/awsui-components-react";
import React from "react";
import MapReleaseModal from "../../components/mapRelease/MapReleaseModal";
import { useMapReleaseStore } from "../../store/MapReleaseStore";
import { getUserAlias } from "../apiCall";
import SpocAdjustmentModal from "../../components/mapRelease/SpocAdjustmentModal";
import ApprovalRulesModal from "../../components/mapRelease/ApprovalRulesModal";

// Aliases authorized to release map packs (members of the transit-estimation-authority POSIX group).
// TODO: Replace with a live POSIX group membership check via a backend endpoint.
//       Note: this client-side list is a UX gate only — the backend release endpoint
//       must also enforce group membership for this to be a real authorization control.
const AUTHORIZED_RELEASE_MEMBERS: ReadonlySet<string> = new Set<string>([
    "laddhah",
    "guptutk",
    "mingdali",
    "yadavdm",
    "nobeshbn",
    "toyemade",
    "ropenta",
    "patelksj",
    "haikunli",
]);

// Region-to-realm mapping per the manual release SOP:
// https://w.amazon.com/bin/view/TIE2/Operations/SOPs/HowToStartProdReleaseManually/
// Note: FE maps to JPAmazon, not FEAmazon.
const REGION_TO_REALM: Readonly<Record<string, string>> = {
    NA: "NAAmazon",
    EU: "EUAmazon",
    FE: "JPAmazon",
    JP: "JPAmazon",
    IN: "INAmazon",
};

export interface MapPackReleaseData {
    rsmVersion: string;
    rsmControlVersion: string
    trecsRecostingLink: string;
    stateID: string;
    rsmDate: string;
    approved: number;
    totalApprovers: number;
    totalPendingApprovals: number;
    releaseStatus: string;
    release: string;
    nestedItems: any;
}

export interface MapPackReleaseStateIDData {
    mapPackVersion: string,
    recostingLink: string,
    summaryId: string,
    validationSummary: string
    validationStatus: string,
    approvalStatus: string,
    approvalLink: string,
}

const ValidationStatus = ({ mapPackVersion }: { mapPackVersion: string }) => {
    const savedClassification = useMapReleaseStore(
        (state) => state.getMapPackBedrockClassification(mapPackVersion)
    );
    
    return savedClassification ? <>{savedClassification}</> : <>Summary Not Generated</>;
}

const ApprovalStatus = ({ mapPackVersion }: { mapPackVersion: string }) => {
    const approvalStatus = useMapReleaseStore((state) => state.getConsensusApprovalStatus());
    
    const matchingApproval = approvalStatus?.find((item: any) => 
        item.mapPackString === mapPackVersion
    );
    if (!matchingApproval) {
        return <>Approval Not Created</>;
    }
    if (matchingApproval.status && matchingApproval.status.trim() !== "") {
        return <>{matchingApproval.status}</>;
    }
    if (matchingApproval.approvalStatus && matchingApproval.approvalStatus.trim() !== "") {
        return <>{matchingApproval.approvalStatus}</>;
    }    
    if (matchingApproval.approvalRules && Array.isArray(matchingApproval.approvalRules)) {
        const allRulesSatisfied = matchingApproval.approvalRules.every((rule: any) => {
            return rule.ruleSatisfied === true;
        });
        return <>{allRulesSatisfied ? "APPROVED" : "PENDING"}</>;
    }
    return <>PENDING</>;
}

const ApprovalButton = ({ mapPackVersion }: { mapPackVersion: string }) => {
    const savedClassification = useMapReleaseStore(
        (state) => state.getMapPackBedrockClassification(mapPackVersion)
    );
    const disabled = !savedClassification || savedClassification.toLowerCase() === "breach";
    
    const [buttonState, setButtonState] = React.useState(false);
    const [linkUrl, setLinkUrl] = React.useState("");
    const fetchConsensusApprovalLink = useMapReleaseStore(
            (state) => state.fetchConsensusApprovalLink
    );
    const activeConsensusApprovalData = useMapReleaseStore(
            (state) => state.getConsensusApprovalLink()
    );
    const currentRegion = useMapReleaseStore(
            (state) => state.currentRegion
    );
    const handleChange = () => {
        if (linkUrl) {
            window.open(linkUrl, '_blank');
            return;
        }
        const consensusBody = JSON.stringify({ 
            "version": "v1", 
            "mapPackString": mapPackVersion,
            "region": currentRegion,
            "requesterAlias": getUserAlias()
        });
        fetchConsensusApprovalLink(consensusBody);
        setButtonState(true);
    }
    React.useEffect(() => {
        if (buttonState && activeConsensusApprovalData) {
            setLinkUrl(activeConsensusApprovalData);
            window.open(activeConsensusApprovalData, '_blank');
            setButtonState(false);
        }
    }, [activeConsensusApprovalData]);
   
    return (
        <Button 
            variant="link"
            iconName="external"
            loading={buttonState}
            disabled={disabled}
            onClick={() => {
                handleChange();
            }}  
        >
            Link
        </Button>
    )
}

const ReleaseButton = ({ mapPackReleaseData}: { mapPackReleaseData: MapPackReleaseData}) => {
    const [buttonIcon, setButtonIcon] = React.useState<IconProps.Name>("status-in-progress");
    const [buttonState, setButtonState] = React.useState(false);
    const [buttonText, setButtonText] = React.useState("");
    const [showWarning, setShowWarning] = React.useState(false);
    const [disableButton, setDisableButton] = React.useState(true);
    const [errorMessage, setErrorMessage] = React.useState("");
    // Synchronous guard against rapid double-clicks — setState is async, so
    // two clicks in the same tick can both enter handleChange before the
    // button re-renders as disabled. A ref updates immediately.
    const hasReleasedRef = React.useRef(false);

    React.useEffect(() => {
        const isAuthorizedReleaser = AUTHORIZED_RELEASE_MEMBERS.has(getUserAlias() ?? "");

        if (mapPackReleaseData.releaseStatus === "READY_FOR_RELEASE") {
            setButtonText("Release");
            if (isAuthorizedReleaser) {
                setButtonIcon("unlocked");
                setDisableButton(false);
                setErrorMessage("");
            } else {
                setButtonIcon("lock-private");
                setDisableButton(true);
                setErrorMessage("You are not authorized to release");
            }
        } else if (mapPackReleaseData.releaseStatus === "INITIATED_RELEASE") {
            setButtonText("Release Initiated");
            setDisableButton(true);
            setButtonIcon("status-pending");
            setErrorMessage("");
        } else if (mapPackReleaseData.releaseStatus === "RELEASED") {
            setButtonText("Release Successful");
            setDisableButton(true);
            setButtonIcon("status-positive");
            setErrorMessage("");
        } else if (mapPackReleaseData.releaseStatus === "BUILD_COMPLETED") {
            setButtonText("Build Completed");
            setDisableButton(true);
            setButtonIcon("status-in-progress");
            setErrorMessage("");
        } else {
            setButtonText("Error");
            setButtonIcon("status-warning");
            setDisableButton(true);
            setErrorMessage("Invalid release status");
        }
    }, [mapPackReleaseData.releaseStatus]);

    const fetchMapPackReleaseSummary = useMapReleaseStore(
            (state) => state.fetchReleaseLambdaOutput
    );
    const activeReleaseData = useMapReleaseStore(
            (state) => state.getReleaseLambdaOutput()
    );
    const currentRegion = useMapReleaseStore((state) => state.currentRegion);
    const handleChange = () => {
        if (hasReleasedRef.current) return;
        const realm = REGION_TO_REALM[currentRegion];
        if (!realm) {
            setButtonText("Release Failed");
            setButtonIcon("status-warning");
            setShowWarning(true);
            setErrorMessage(`No realm configured for region ${currentRegion}`);
            return;
        }
        hasReleasedRef.current = true;
        const releasePayload = JSON.stringify({
            realm,
            operationType: "RELEASE_NEW_MAP_VERSION",
            stateId: mapPackReleaseData.stateID,
        });
        fetchMapPackReleaseSummary(releasePayload);
        setButtonState(true);
        setDisableButton(true);
        setButtonText("Release Initiated");
        setButtonIcon("status-pending");
    }
    React.useEffect(() => {
        if (buttonState && activeReleaseData[0] === false) {
            // Failure: re-enable so the user can retry without a page refresh.
            hasReleasedRef.current = false;
            setButtonState(false);
            setDisableButton(false);
            setButtonText("Release Failed");
            setButtonIcon("status-warning");
            setShowWarning(true);
            setErrorMessage(activeReleaseData[1]);
        } else if (buttonState && activeReleaseData[0] === true) {
            setButtonState(false);
            setDisableButton(true);
            setErrorMessage("");
        }
    }, [activeReleaseData]);
    
    return (
        <div style={{ maxWidth: '100%', width: '200px' }}>
        <Button 
            disabled={disableButton}
            loading={buttonState}
            onClick={() => {
                handleChange();
                console.log(`Button clicked for ${mapPackReleaseData.rsmVersion}`);
            }}
            wrapText={true}
            variant="primary"
            iconName={buttonIcon}
        >
            {buttonText}
        </Button>
        {showWarning && (
               <Box
                    color="text-status-error"
                    fontSize="body-s"
                    margin={{ top: "xs" }}
                    padding={{ bottom: "xs" }}
                >
                    <div style={{ 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        width: '300px'
                    }}>
                        Error: {errorMessage}
                    </div>
                </Box>
            )}
        </div>
    );
};

const MCMButton = ({mapPackReleaseData}: {mapPackReleaseData: MapPackReleaseData}) => {
    const [buttonState, setButtonState] = React.useState(false);
    const [linkUrl, setLinkUrl] = React.useState("");
    const fetchMCMLink = useMapReleaseStore(
            (state) => state.fetchMCMLink
    );
    const activeMCMData = useMapReleaseStore(
            (state) => state.getMCMLink()
    );
    const currentRegion = useMapReleaseStore(
            (state) => state.currentRegion
    );
    const handleChange = () => {
        if (linkUrl) {
            window.open(linkUrl, '_blank');
            return;
        }
        const mcmBody = JSON.stringify({
            "region": currentRegion,
            "currentRSMVersion": mapPackReleaseData.rsmControlVersion,
            "newRSMVersion": mapPackReleaseData.rsmVersion,
            "trecLink": mapPackReleaseData.trecsRecostingLink,
            "requesterAlias": getUserAlias()
        });
        fetchMCMLink(mcmBody);
        setButtonState(true);
    }
    React.useEffect(() => {
        if (buttonState && activeMCMData) {
            setLinkUrl(activeMCMData);
            window.open(activeMCMData, '_blank');
            setButtonState(false);
        }
    }, [activeMCMData]);
   
    return (
        <Button 
            variant="link"
            iconName="external"
            loading={buttonState}
            onClick={() => {
                handleChange();
            }}  
        >
            Link
        </Button>
    )
}

const bedrockPrompt = (mapPackVersion: string) => {
    const promptText = "\\n\\nHuman: Your job is to analyze and classify a newly generated MapPack dataset that will be validated before released into Amazon Maps. Based on these thresholds listed, you will either classify the MapPack data as \"Validation Successful - No Breach\", \"Validation Successful with warnings - No Breach\", \"SPOC adjustment\", or \"Breach\". For a MapPack to be classified as \"Validation Successful - No Breach\", there should be no transit time breached stations, PNF % Change should be less than 1% increase and have 0 STEM path not found trips. For a classification of \"Validation Successful with warnings - No Breach\", there should be no transit time breached stations, PNF % Change should be greater than 1% but less than 10% increase and have 0 STEM path not found trips. For a classification of \"SPOC adjustment\", the number of transit time breached stations is less than 5% of in total stations, and there shouldn't be any STEM path not found trips. For a classification of \"Breach\", there is more than 0 path not found on STEM trips or the number of transit time breached stations exceeds 5% of in total stations. \\n\\nCRITICAL: For PNF % Change, use ONLY the 'pnfPercentIncrease' field. If this field is missing, null, or undefined, output 'PNF % Change: Data not available' and exclude PNF from classification. NEVER use 'dpmoChange', 'dpmo', or any other field as substitute.\\n\\nIMPORTANT: When PNF % Change data is not available, the classifications 'Validation Successful - No Breach' and 'Validation Successful with warnings - No Breach' have the same criteria (no transit time breached stations and 0 STEM path not found trips). In such cases, always classify as 'Validation Successful - No Breach'.\\n\\nPlease strictly follow this template on what to output: MapPack Version:, Control version:, Region:, Total Stations:, Transit Time Breaching Percentage:, PNF % Change:, StemPathNotFound trips:, Classification:, Classification Explanation:. If PNF data is not available, explain in the Classification Explanation that PNF data was not available and classification was based only on other criteria. Lastly, include your classification in exact json format of \"{\\\"mapPackClassification\\\": \\\"Breach\\\"}\". \\n\\nAssistant:";
    
    const prompt = {
        "prompt": promptText,
        "modelId": "us.anthropic.claude-3-5-sonnet-20240620-v1:0",
        "mapPackString": mapPackVersion
    };
    
    return JSON.stringify(prompt);
}


const MAP_PACK_RELEASE_STATE_ID_TABLE_CONTENT_CONFIG: TableContentConfigProps<MapPackReleaseStateIDData>[] = 
    [
        {
            id: "mapPackVersion",
            description: "Map Pack Version",
            tableCell: (mapPackReleaseData: MapPackReleaseStateIDData) => (
                <Box>{mapPackReleaseData.mapPackVersion}</Box>
            ),
            sortingField: "mapPackVersion",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "validationSummary",
            description: "Validation Results",
            tableCell: (mapPackReleaseData: MapPackReleaseStateIDData) => (
                <Box>
                    <MapReleaseModal bedrockPrompt={bedrockPrompt(mapPackReleaseData.mapPackVersion)} 
                    mapPackVersion={mapPackReleaseData.mapPackVersion}></MapReleaseModal>
                    <Box margin={{ top: "xs" }}>
                        <Box>
                            <a href={"/station-recosting-summary?id=" + mapPackReleaseData.summaryId}>Recosting Link</a>
                        </Box>
                        <Box margin={{ top: "xs" }}>
                            <a href={"/station-simulation-summary?id=" + mapPackReleaseData.summaryId}>Simulation Link</a>
                        </Box>
                    </Box>
                </Box>
            ),
            sortingField: "validationSummary",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "spocAdjustment",
            description: "SPOC Adjustment",
            tableCell: (mapPackReleaseData: MapPackReleaseStateIDData) => (
                <SpocAdjustmentModal mapPack={mapPackReleaseData.mapPackVersion}></SpocAdjustmentModal>
            ),
            sortingField: "spocAdjustment",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "approvalStatus",
            description: "Approval Status",
            tableCell: (mapPackReleaseData: MapPackReleaseStateIDData) => (
                <ApprovalStatus mapPackVersion={mapPackReleaseData.mapPackVersion}></ApprovalStatus>
            ),
            sortingField: "approvalStatus",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "approvalLink",
            description: "Approval Link",
            tableCell: (mapPackReleaseData: MapPackReleaseStateIDData) => (
                <ApprovalButton mapPackVersion={mapPackReleaseData.mapPackVersion}></ApprovalButton>
            ),
            sortingField: "approvalLink",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "approvalRules",
            description: "Approval Rules",
            tableCell: (mapPackReleaseData: MapPackReleaseStateIDData) => (
                <ApprovalRulesModal mapPackVersion={mapPackReleaseData.mapPackVersion}></ApprovalRulesModal>
            ),
            sortingField: "approvalRules",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
    ];
    

const MAP_PACK_RELEASE_SUMMARY_TABLE_CONTENT_CONFIG: TableContentConfigProps<MapPackReleaseData>[] =
    [
        {
            id: "rsmVersion",
            description: "RSM Version",
            tableCell: (mapPackReleaseData: MapPackReleaseData) => (
                <Box>{mapPackReleaseData.rsmVersion}</Box>
            ),
            sortingField: "rsmVersion",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "stateID",
            description: "State ID",
            tableCell: (mapPackReleaseData: MapPackReleaseData) => (
                <Box>{mapPackReleaseData.stateID || 'N/A'}</Box>
            ),
            sortingField: "stateID",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "rsmDate",
            description: "Date of Latest Change Set",
            tableCell: (mapPackReleaseData: MapPackReleaseData) => (
                <Box>{mapPackReleaseData.rsmVersion.substring(0,4)}-{mapPackReleaseData.rsmVersion.substring(4,6)}-{mapPackReleaseData.rsmVersion.substring(6,8)}</Box>
            ),
            sortingField: "stateID",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "experimentApproved",
            description: "Experiments Approved",
            tableCell: (mapPackReleaseData: MapPackReleaseData) => (
                <Box>{`${mapPackReleaseData.approved} / ${mapPackReleaseData.totalApprovers}`}</Box>
            ),
            sortingField: "experimentApproved",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "MCM",
            description: "MCM",
            tableCell: (mapPackReleaseData: MapPackReleaseData) => (
                <MCMButton mapPackReleaseData={mapPackReleaseData}></MCMButton>
            ),
            sortingField: "MCM",
            filterGroup: "mapPackReleaseTableProp",
            filterOperators: TEXT_FIELD_OPERATORS,
        },
        {
            id: "release",
            description: "Release",
            tableCell: (mapPackReleaseData: MapPackReleaseData) => {
                return <ReleaseButton mapPackReleaseData={mapPackReleaseData}/>
            }
        },
        
    ];

export const MAP_PACK_RELEASE_SUMMARY_TABLE_COLUMN_DEFINITIONS =
    configPropsToColumnDefinitions(MAP_PACK_RELEASE_SUMMARY_TABLE_CONTENT_CONFIG);

export const MAP_PACK_RELEASE_STATE_ID_TABLE_COLUMN_DEFINITIONS =
    configPropsToColumnDefinitions(MAP_PACK_RELEASE_STATE_ID_TABLE_CONTENT_CONFIG);

export const MAP_PACK_RELEASE_SUMMARY_TABLE_VISIBLE_CONTENT_OPTIONS: any = [
    {
        label: "Map Release Management Dashboard",
        options: columnDefinitionToOption(MAP_PACK_RELEASE_SUMMARY_TABLE_COLUMN_DEFINITIONS),
    },
];

export const MAP_PACK_RELEASE_SUMMARY_TABLE_FILTER_PROPERTIES: FilteringProperty[] =
    configPropsToFilterProps(MAP_PACK_RELEASE_SUMMARY_TABLE_CONTENT_CONFIG).sort((a, b) =>
        a.propertyLabel.localeCompare(b.propertyLabel)
    );

export const PAGE_SIZE_OPTIONS: CollectionPreferencesProps.PageSizeOption[] = [
    {value: 1, label: "1 Entries"},
    {value: 2, label: "2 Entries"},
    {value: 3, label: "3 Entries"},
    {value: 4, label: "4 Entries"},
    {value: 5, label: "5 Entries"},
    {value: 10, label: "10 Entries"},
    {value: 20, label: "20 Entries"},
    {value: 50, label: "50 Entries"},
    {value: 100, label: "100 Entries"},
];
